import { ListBulletIcon } from '@radix-ui/react-icons';
import { Flex, Select, Text } from '@radix-ui/themes';
import { FormHint } from 'components/form/hint';
import { FormInputWrapper } from 'components/form/input-wrapper';
import { FormLabel } from 'components/form/label';
import { EB } from 'components/layout/error-boundary';
import { FormContext } from 'contexts/form';
import { InputErrorType } from 'enums/form';
import { RX } from 'enums/radix';
import { FormInputDef } from 'interfaces/form/input';
import {
  EMPTY_SELECT_VALUE,
  extendedErrorDetection,
  FormInputChangeDef,
} from 'interfaces/form/input-change';
import { FormInputWrapperDef } from 'interfaces/form/input-wrapper';
import { OptionDef } from 'interfaces/options';
import { SortUtil } from 'lib/utilities/sort';
import { StringUtil } from 'lib/utilities/string';
import _ from 'lodash';
import { ReactNode, useContext, useEffect, useMemo, useState } from 'react';

export const FormSelect = (
  props: FormInputDef &
    FormInputWrapperDef &
    FormInputChangeDef & {
      options: OptionDef[];

      defaultValue?: string;
      slot?: ReactNode;

      skipSort?: boolean;
    }
) => {
  const { lastSubmitted, addError, removeError } = useContext(FormContext);

  const [touched, setTouched] = useState(!props.defaultTouched);

  const [value, setValue] = useState(props.defaultValue ?? EMPTY_SELECT_VALUE);

  const sortedOptions = useMemo(() => {
    const groupDict = _.groupBy(props.options, (o) => o.group);

    const multipleGroups = Object.keys(groupDict).length > 1;

    const groupKeys = Object.keys(groupDict);

    if (!props.skipSort) {
      groupKeys.sort((a, b) => SortUtil.strings(a, b));
    }

    return groupKeys.flatMap((group) => {
      const gOptions: OptionDef[] = groupDict[group];

      if (!props.skipSort) {
        gOptions.sort((a, b) => SortUtil.strings(a.label, b.label));
      }

      // only need group entries if there is more than one group
      if (multipleGroups) {
        gOptions.splice(0, 0, {
          label: group,
          value: `--${group}--`,
          // add the group label as the first option
          isGroupLabel: true,
        });
      }

      return gOptions;
    });
  }, [props.options]);

  const errorType = useMemo(() => {
    if (props.required && !value.trim()) {
      return InputErrorType.Required;
    }

    if (
      value !== EMPTY_SELECT_VALUE &&
      sortedOptions.findIndex((o) => o.value === value) === -1
    ) {
      return InputErrorType.InvalidSelection;
    }

    return extendedErrorDetection(value, props) ?? InputErrorType.None;
  }, [value, sortedOptions]);

  useEffect(() => {
    if (!props.label) {
      return;
    }

    (errorType === InputErrorType.None ? removeError : addError)(props.label);
  }, [props.label, errorType]);

  const onValueChange = (v: string) => {
    setValue(v);
    setTouched(true);

    if (props.onChange) {
      if (v !== EMPTY_SELECT_VALUE) {
        props.onChange(v);
      }
    }

    if (props.onOptionalChange) {
      props.onOptionalChange(v === EMPTY_SELECT_VALUE ? undefined : v);
    }

    const safeNum = StringUtil.tryParseNumber(v);

    if (props.onNumericChange && safeNum !== undefined) {
      props.onNumericChange(safeNum);
    }

    if (props.onOptionalNumericChange) {
      props.onOptionalNumericChange(safeNum);
    }

    if (props.onBooleanChange) {
      props.onBooleanChange(v === 'true');
    }

    if (props.onOptionalBooleanChange) {
      props.onOptionalBooleanChange(
        v === EMPTY_SELECT_VALUE ? undefined : v === 'true'
      );
    }
  };

  return (
    <EB>
      <FormInputWrapper {...props}>
        <FormLabel {...props} />

        <Select.Root
          value={value}
          disabled={props.disabled}
          onValueChange={onValueChange}
        >
          <Select.Trigger
            placeholder={`Select a value for ${props.label}`}
            style={{ width: '100%' }}
            onContextMenu={(e) => {
              e.preventDefault();
              onValueChange(EMPTY_SELECT_VALUE);
            }}
          />
          <Select.Content>
            <Select.Item value={EMPTY_SELECT_VALUE}>
              <Text color={RX.CLR.NEUTRAL}>
                {props.placeholder ?? 'Select a value'}
              </Text>
            </Select.Item>

            {sortedOptions.map((o, i) => (
              <Select.Item
                key={`o-${i}`}
                value={o.value}
                disabled={o.disabled || o.isGroupLabel}
              >
                <Flex align="center" gap="1">
                  {o.isGroupLabel && <ListBulletIcon />}
                  <Text truncate>{o.label}</Text>
                </Flex>
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>

        <FormHint
          hint={props.hint}
          error={errorType}
          touched={touched || !!lastSubmitted}
        />
      </FormInputWrapper>
    </EB>
  );
};
